// lib/structured-output-schema.js — STRUCTURED OUTPUT SCHEMAS
// ═══════════════════════════════════════════════════════════
// Phase 3 Trust — shared schemas for grounded answers.
//
// Two schemas, one for single-fact claims and one for multi-finding
// analysis. Both include explicit confidence and source fields so the
// UI can render trust signals (badges, citation chips) instead of
// presenting opinion as fact.
//
// Validators are plain JS — no zod dependency. Returns
// { ok: true, value } or { ok: false, errors }.
//
// All grounded responses are persisted to the messages table with
// the SHIFT_GROUNDED_V1: prefix so the renderer can detect them and
// dispatch to the GroundedAnswerCard component.
// ═══════════════════════════════════════════════════════════

export const GROUNDED_PREFIX = 'SHIFT_GROUNDED_V1:';
export const VALID_TYPES = ['claim', 'analysis'];
export const VALID_CONFIDENCE = ['high', 'medium', 'low'];
export const VALID_VERIFICATION_STATUS = ['clean', 'flagged', 'unchecked'];

// ── Claim schema ───────────────────────────────────────────
// Single-fact answer. Use this when the user asks one factual
// question with a discrete answer.
//
// {
//   "type": "claim",
//   "answer_text": "...",
//   "confidence": "high" | "medium" | "low",
//   "sources": [{ "label": "...", "url": "...", "snippet": "..." }],
//   "caveats": "...",       (optional)
//   "model_used": "..."     (filled in by handler, not the model)
// }
function validateClaim(payload) {
  const errors = [];
  if (typeof payload.answer_text !== 'string' || payload.answer_text.trim().length < 3) {
    errors.push('answer_text required (≥3 chars)');
  }
  if (!VALID_CONFIDENCE.includes(payload.confidence)) {
    errors.push(`confidence must be one of: ${VALID_CONFIDENCE.join(', ')}`);
  }
  if (!Array.isArray(payload.sources)) {
    errors.push('sources must be an array (may be empty)');
  } else {
    payload.sources.forEach((src, i) => {
      if (typeof src !== 'object' || src === null) {
        errors.push(`sources[${i}] must be an object`);
        return;
      }
      if (typeof src.label !== 'string' || !src.label.trim()) {
        errors.push(`sources[${i}].label required`);
      }
      // reachable is added by the handler post-validation; tolerated either way
    });
  }
  if (payload.caveats !== undefined && typeof payload.caveats !== 'string') {
    errors.push('caveats must be a string if present');
  }
  // verification is added by the handler post-validation; tolerated either way
  return errors;
}

// ── Analysis schema ────────────────────────────────────────
// Multi-finding output. Use this when the user asks something that
// produces several findings with their own confidence levels.
//
// {
//   "type": "analysis",
//   "summary": "...",
//   "findings": [
//     { "claim": "...", "confidence": "...", "evidence": "..." }
//   ],
//   "open_questions": ["..."],
//   "model_used": "..."
// }
function validateAnalysis(payload) {
  const errors = [];
  if (typeof payload.summary !== 'string' || payload.summary.trim().length < 3) {
    errors.push('summary required (≥3 chars)');
  }
  if (!Array.isArray(payload.findings) || payload.findings.length === 0) {
    errors.push('findings must be a non-empty array');
  } else {
    payload.findings.forEach((f, i) => {
      if (typeof f !== 'object' || f === null) {
        errors.push(`findings[${i}] must be an object`);
        return;
      }
      if (typeof f.claim !== 'string' || !f.claim.trim()) {
        errors.push(`findings[${i}].claim required`);
      }
      if (!VALID_CONFIDENCE.includes(f.confidence)) {
        errors.push(`findings[${i}].confidence must be one of: ${VALID_CONFIDENCE.join(', ')}`);
      }
      if (typeof f.evidence !== 'string' || !f.evidence.trim()) {
        errors.push(`findings[${i}].evidence required`);
      }
    });
  }
  if (payload.open_questions !== undefined && !Array.isArray(payload.open_questions)) {
    errors.push('open_questions must be an array if present');
  }
  return errors;
}

// ── Top-level validator ────────────────────────────────────
export function validateGroundedPayload(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ok: false, errors: ['payload must be an object'] };
  }
  if (!VALID_TYPES.includes(raw.type)) {
    return { ok: false, errors: [`type must be one of: ${VALID_TYPES.join(', ')}`] };
  }
  const errors = raw.type === 'claim'
    ? validateClaim(raw)
    : validateAnalysis(raw);
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, value: raw };
}

// ── Encode / decode for the messages table ─────────────────
// Grounded responses are persisted as a single string with the
// SHIFT_GROUNDED_V1: prefix followed by the JSON payload. Renderer
// detects the prefix and dispatches to the GroundedAnswerCard.
export function encodeGrounded(payload) {
  return `${GROUNDED_PREFIX}${JSON.stringify(payload)}`;
}

export function decodeGrounded(content) {
  if (typeof content !== 'string') return null;
  if (!content.startsWith(GROUNDED_PREFIX)) return null;
  try {
    const json = content.slice(GROUNDED_PREFIX.length);
    const parsed = JSON.parse(json);
    const result = validateGroundedPayload(parsed);
    return result.ok ? result.value : null;
  } catch (_) {
    return null;
  }
}

export function isGroundedContent(content) {
  return typeof content === 'string' && content.startsWith(GROUNDED_PREFIX);
}
